import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { catalogAPI } from '../../services/api';
import './CatalogAdmin.css';

/* ── Ícones disponíveis — imagens em /public/icones/ ── */
const ICON_OPTIONS = [
  { src: '/icones/agua.png',     label: 'Água'     },
  { src: '/icones/drenagem.png', label: 'Drenagem' },
  { src: '/icones/esgoto.png',   label: 'Esgoto'   },
  { src: '/icones/residuos.png', label: 'Resíduos' },
  { src: '/icones/territoriodemografia.png', label: 'Território e Demografia' },
  { src: '/icones/governanca.png', label: 'Governança' },
];

const DEFAULT_ICON = '/icones/agua.png';

function GroupIcon({ icon, size = 24 }) {
  if (!icon) return <span style={{ fontSize: Math.round(size * 0.75) }}>📁</span>;
  if (icon.startsWith('/icones/')) {
    const label = ICON_OPTIONS.find((o) => o.src === icon)?.label || 'ícone';
    return (
      <img src={icon} alt={label}
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }} />
    );
  }
  return <span>{icon}</span>;
}

function getIconLabel(icon) {
  if (!icon) return '';
  const opt = ICON_OPTIONS.find((o) => o.src === icon);
  return opt ? `[${opt.label}]` : icon;
}

function IconPicker({ value, onChange }) {
  return (
    <div className="ca-icon-picker">
      {ICON_OPTIONS.map((opt) => (
        <button key={opt.src} type="button"
          className={`ca-icon-opt${value === opt.src ? ' ca-icon-opt--active' : ''}`}
          onClick={() => onChange(opt.src)} aria-label={opt.label} title={opt.label}>
          <img src={opt.src} alt={opt.label} className="ca-icon-opt-img" />
          <span className="ca-icon-opt-label">{opt.label}</span>
        </button>
      ))}
    </div>
  );
}

function countFolderLayers(folder) {
  return (folder.layers || []).length +
    (folder.folders || []).reduce((s, f) => s + countFolderLayers(f), 0);
}

function findFolderById(folders, id) {
  for (const f of folders) {
    if (f.id === id) return f;
    const found = findFolderById(f.folders || [], id);
    if (found) return found;
  }
  return null;
}

// ─── FolderPathPicker ─────────────────────────────────────────────────────────

function FolderPathPicker({ category, onChange }) {
  const [levels,   setLevels]   = useState(['']);
  const [newNames, setNewNames] = useState({});

  function getFoldersAtDepth(depth) {
    if (depth === 0) return category.folders || [];
    const parentId = parseInt(levels[depth - 1]);
    if (!parentId) return [];
    return findFolderById(category.folders || [], parentId)?.folders || [];
  }

  function handleSelect(depth, value) {
    const newLevels = [...levels.slice(0, depth), value];
    if (value && value !== '' && value !== '__new__') newLevels.push('');
    const updatedNames = { ...newNames };
    Object.keys(updatedNames).forEach((k) => { if (parseInt(k) >= depth) delete updatedNames[k]; });
    setLevels(newLevels);
    setNewNames(updatedNames);
    onChange(newLevels, updatedNames);
  }

  function handleNewName(depth, value) {
    const updated = { ...newNames, [depth]: value };
    setNewNames(updated);
    onChange(levels, updated);
  }

  const depthsToShow = levels.reduce((acc, val, i) => {
    const prev = i > 0 ? levels[i - 1] : 'ROOT';
    if (i === 0 || (prev && prev !== '' && prev !== '__new__')) acc.push(i);
    return acc;
  }, []);

  return (
    <div className="ca-folder-picker">
      {depthsToShow.map((depth) => {
        const folders  = getFoldersAtDepth(depth);
        const selected = levels[depth] || '';
        const label    = depth === 0 ? 'Pasta (opcional)' : `↳ Sub-pasta nível ${depth + 1}`;
        return (
          <div key={depth} className="ca-folder-level">
            <label className="ca-label ca-label--sm">{label}</label>
            <select className="ca-select ca-select--sm" value={selected}
              onChange={(e) => handleSelect(depth, e.target.value)}>
              <option value="">{depth === 0 ? '— diretamente na categoria —' : '— diretamente aqui —'}</option>
              {folders.map((f) => <option key={f.id} value={String(f.id)}>{f.name}</option>)}
              <option value="__new__">+ Criar nova pasta…</option>
            </select>
            {selected === '__new__' && (
              <input className="ca-input ca-input--sm"
                placeholder={depth === 0 ? 'Nome da nova pasta' : 'Nome da sub-pasta'}
                value={newNames[depth] || ''} onChange={(e) => handleNewName(depth, e.target.value)} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── PendingRow ───────────────────────────────────────────────────────────────

function PendingRow({ layer, groups, onAssigned, onGroupCreated, onCategoryCreated }) {
  const [open,         setOpen]         = useState(false);
  const [groupId,      setGroupId]      = useState('');
  const [categoryId,   setCategoryId]   = useState('');
  const [newGroup,     setNewGroup]     = useState('');
  const [newGroupIcon, setNewGroupIcon] = useState(DEFAULT_ICON);
  const [newCategory,  setNewCategory]  = useState('');
  const [title,        setTitle]        = useState('');
  const [description,  setDescription]  = useState('');
  const [folderLevels, setFolderLevels] = useState(['']);
  const [folderNames,  setFolderNames]  = useState({});
  const [saving,       setSaving]       = useState(false);
  const [err,          setErr]          = useState('');

  const selectedGroup = groups.find((g) => String(g.id) === String(groupId));
  const categories    = selectedGroup?.categories || [];
  const selectedCat   = categories.find((c) => String(c.id) === String(categoryId));

  async function resolveFolderPath(targetCategoryId) {
    let parentFolderId = null;
    for (let i = 0; i < folderLevels.length; i++) {
      const val = folderLevels[i];
      if (!val || val === '') break;
      if (val === '__new__') {
        const name = (folderNames[i] || '').trim();
        if (!name) throw new Error(`Nome da pasta no nível ${i + 1} é obrigatório.`);
        const res = await catalogAPI.createFolder({ category_id: targetCategoryId, parent_id: parentFolderId, name });
        parentFolderId = res.data.id;
      } else {
        parentFolderId = parseInt(val);
      }
    }
    return parentFolderId;
  }

  async function handleConfirm() {
    setErr('');
    let targetGroupId    = groupId;
    let targetCategoryId = categoryId;
    try {
      setSaving(true);
      if (groupId === '__new__') {
        if (!newGroup.trim()) { setErr('Nome do grupo é obrigatório.'); setSaving(false); return; }
        const res = await catalogAPI.createGroup({ name: newGroup.trim(), icon: newGroupIcon });
        targetGroupId = res.data.id;
        onGroupCreated(res.data);
      }
      if (categoryId === '__new__') {
        if (!newCategory.trim()) { setErr('Nome da categoria é obrigatório.'); setSaving(false); return; }
        const res = await catalogAPI.createCategory({ group_id: targetGroupId, name: newCategory.trim() });
        targetCategoryId = res.data.id;
        onCategoryCreated(targetGroupId, res.data);
      }
      if (!targetGroupId || !targetCategoryId) { setErr('Selecione ou crie um grupo e uma categoria.'); setSaving(false); return; }
      let folderId = null;
      try { folderId = await resolveFolderPath(targetCategoryId); }
      catch (folderErr) { setErr(folderErr.message); setSaving(false); return; }
      await catalogAPI.assign({
        category_id: targetCategoryId, folder_id: folderId, layer_id: layer.id,
        title: title.trim() || undefined, description: description.trim() || undefined,
      });
      onAssigned(layer.id);
    } catch (e) {
      setErr(e.response?.data?.error || 'Erro ao atribuir camada.');
    } finally { setSaving(false); }
  }

  return (
    <div className={`pending-row${open ? ' pending-row--open' : ''}`}>
      <div className="pending-row-main">
        <div className="pending-row-info">
          <span className="pending-layer-name">{layer.name}</span>
          <span className="pending-layer-ws">{layer.workspace}</span>
        </div>
        <button className="pending-assign-btn" onClick={() => setOpen((v) => !v)}>
          {open ? 'Cancelar' : 'Atribuir'}
        </button>
      </div>
      {open && (
        <div className="pending-assign-form">
          <div className="ca-form-row">
            <label className="ca-label" htmlFor={`grp-${layer.id}`}>Grupo</label>
            <select id={`grp-${layer.id}`} className="ca-select" value={groupId}
              onChange={(e) => { setGroupId(e.target.value); setCategoryId(''); setFolderLevels(['']); setFolderNames({}); }}>
              <option value="">— selecione —</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{getIconLabel(g.icon)} {g.name}</option>)}
              <option value="__new__">+ Criar novo grupo…</option>
            </select>
            {groupId === '__new__' && (
              <>
                <input className="ca-input" placeholder="Nome do novo grupo"
                  value={newGroup} onChange={(e) => setNewGroup(e.target.value)} />
                <p className="ca-label" style={{ marginTop: 6 }}>Ícone do grupo</p>
                <IconPicker value={newGroupIcon} onChange={setNewGroupIcon} />
              </>
            )}
          </div>
          {(groupId && groupId !== '__new__') && (
            <div className="ca-form-row">
              <label className="ca-label" htmlFor={`cat-${layer.id}`}>Categoria</label>
              <select id={`cat-${layer.id}`} className="ca-select" value={categoryId}
                onChange={(e) => { setCategoryId(e.target.value); setFolderLevels(['']); setFolderNames({}); }}>
                <option value="">— selecione —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value="__new__">+ Criar nova categoria…</option>
              </select>
              {categoryId === '__new__' && (
                <input className="ca-input" placeholder="Nome da nova categoria"
                  value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
              )}
            </div>
          )}
          {groupId === '__new__' && (
            <div className="ca-form-row">
              <label className="ca-label">Categoria</label>
              <input className="ca-input" placeholder="Nome da nova categoria"
                value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
            </div>
          )}
          {categoryId && categoryId !== '__new__' && selectedCat && (
            <div className="ca-form-row">
              <div className="ca-folder-picker-label">
                <span className="ca-label">Localização na pasta</span>
                <span className="ca-label-hint">Opcional</span>
              </div>
              <FolderPathPicker key={categoryId} category={selectedCat}
                onChange={(levels, names) => { setFolderLevels(levels); setFolderNames(names); }} />
            </div>
          )}
          <div className="ca-form-row">
            <label className="ca-label" htmlFor={`title-${layer.id}`}>Título (nome na legenda)</label>
            <input id={`title-${layer.id}`} className="ca-input"
              placeholder="Ex: Plano de Gerenciamento de RSS"
              value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="ca-form-row">
            <label className="ca-label" htmlFor={`desc-${layer.id}`}>Fonte</label>
            <input id={`desc-${layer.id}`} className="ca-input"
              placeholder="Ex: IBGE 2022, ANA 2023…"
              value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {err && <p className="ca-error">{err}</p>}
          <button className="ca-confirm-btn" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Salvando…' : 'Confirmar atribuição'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── LayerRow ─────────────────────────────────────────────────────────────────

function LayerRow({ layer, onDeleteAssignment }) {
  return (
    <div className="ca-tree-layer">
      <div className="ca-tree-layer-info">
        <span className="ca-tree-layer-name">{layer.title || layer.layerName}</span>
        {layer.description && <span className="ca-tree-layer-desc">{layer.description}</span>}
      </div>
      <span className="ca-tree-layer-ws">{layer.workspace}</span>
      <button className="ca-tree-btn ca-tree-btn--danger" title="Remover atribuição"
        onClick={() => onDeleteAssignment(layer.id, layer.layerName)}>✕</button>
    </div>
  );
}

// ─── TreeFolder (recursivo) ───────────────────────────────────────────────────

function TreeFolder({
  folder, categoryId, dragHandle,
  expandSig, collapseSig,
  onDeleteAssignment, onDeleteFolder, onRenameFolder, onAddFolder, onReorderFolders,
}) {
  const sensors = useSensors(useSensor(PointerSensor));
  const [open,         setOpen]         = useState(false);
  const [editingName,  setEditingName]  = useState(false);
  const [draftName,    setDraftName]    = useState(folder.name);
  const [addSubOpen,   setAddSubOpen]   = useState(false);
  const [newSubName,   setNewSubName]   = useState('');
  const [saving,       setSaving]       = useState(false);
  const [subExpandSig,  setSubExpandSig]  = useState(0);
  const [subCollapseSig, setSubCollapseSig] = useState(0);

  /* Responde a sinais recursivos do pai */
  useEffect(() => {
    if (expandSig > 0) { setOpen(true); setSubExpandSig((s) => s + 1); }
  }, [expandSig]);
  useEffect(() => {
    if (collapseSig > 0) { setOpen(false); setSubCollapseSig((s) => s + 1); }
  }, [collapseSig]);

  async function saveRename() {
    if (!draftName.trim() || draftName === folder.name) { setEditingName(false); return; }
    setSaving(true);
    try   { await onRenameFolder(folder.id, draftName.trim()); }
    catch { setDraftName(folder.name); }
    finally { setSaving(false); setEditingName(false); }
  }

  async function handleAddSub() {
    if (!newSubName.trim()) return;
    setSaving(true);
    try { await onAddFolder(categoryId, folder.id, newSubName.trim()); setNewSubName(''); setAddSubOpen(false); }
    finally { setSaving(false); }
  }

  const subFolders = folder.folders || [];

  function handleSubFoldersDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    const oldIdx = subFolders.findIndex((f) => f.id === active.id);
    const newIdx = subFolders.findIndex((f) => f.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorderFolders(subFolders, oldIdx, newIdx);
  }

  const total = countFolderLayers(folder);

  return (
    <div className="ca-tree-folder">
      <div className="ca-tree-folder-header">
        {dragHandle}
        <button className="ca-tree-chevron" onClick={() => setOpen((v) => !v)}
          aria-expanded={open} aria-label={open ? 'Recolher pasta' : 'Expandir pasta'}>
          {open ? '▾' : '▸'}
        </button>
        <span className="ca-tree-folder-icon" aria-hidden="true">📁</span>

        {editingName ? (
          <input className="ca-tree-edit-input" value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={saveRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRename();
              if (e.key === 'Escape') { setDraftName(folder.name); setEditingName(false); }
            }}
            autoFocus disabled={saving} />
        ) : (
          <span className="ca-tree-folder-name">{folder.name}</span>
        )}

        <span className="ca-tree-count">{total}</span>
        <div className="ca-tree-actions">
          <button className="ca-tree-btn ca-tree-btn--rename" title="Renomear pasta"
            onClick={() => setEditingName(true)}>✏️</button>
          <button className="ca-tree-btn" title="Criar sub-pasta"
            onClick={() => setAddSubOpen((v) => !v)}>+ Sub</button>
          <button className="ca-tree-btn ca-tree-btn--danger" title="Remover pasta"
            onClick={() => onDeleteFolder(folder.id)}>✕</button>
        </div>
      </div>

      {addSubOpen && (
        <div className="ca-add-row ca-add-row--indent">
          <input className="ca-input ca-input--inline" placeholder="Nome da sub-pasta"
            value={newSubName} onChange={(e) => setNewSubName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddSub(); }} />
          <button className="ca-confirm-btn ca-confirm-btn--sm"
            onClick={handleAddSub} disabled={saving || !newSubName.trim()}>Criar</button>
        </div>
      )}

      {open && (
        <div className="ca-tree-folder-body">
          {(folder.layers || []).map((layer) => (
            <LayerRow key={layer.id} layer={layer} onDeleteAssignment={onDeleteAssignment} />
          ))}
          {subFolders.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSubFoldersDragEnd}>
              <SortableContext items={subFolders.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                {subFolders.map((sub) => (
                  <SortableTreeFolder key={sub.id} folder={sub} categoryId={categoryId}
                    expandSig={subExpandSig} collapseSig={subCollapseSig}
                    onDeleteAssignment={onDeleteAssignment}
                    onDeleteFolder={onDeleteFolder}
                    onRenameFolder={onRenameFolder}
                    onAddFolder={onAddFolder}
                    onReorderFolders={onReorderFolders}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : null}
          {folder.layers.length === 0 && subFolders.length === 0 && (
            <p className="ca-tree-empty ca-tree-empty--indent">Pasta vazia.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── TreeCategory ─────────────────────────────────────────────────────────────

function TreeCategory({
  category, dragHandle,
  expandSig, collapseSig,
  onDeleteAssignment, onRenameCategory, onDeleteCategory,
  onAddFolder, onDeleteFolder, onRenameFolder, onReorderFolders,
}) {
  const sensors = useSensors(useSensor(PointerSensor));
  const [open,            setOpen]            = useState(false);
  const [editingName,     setEditingName]     = useState(false);
  const [draftName,       setDraftName]       = useState(category.name);
  const [addFolderOpen,   setAddFolderOpen]   = useState(false);
  const [newFolderName,   setNewFolderName]   = useState('');
  const [saving,          setSaving]          = useState(false);
  const [folderExpandSig,  setFolderExpandSig]  = useState(0);
  const [folderCollapseSig, setFolderCollapseSig] = useState(0);

  /* Responde a sinais recursivos do pai */
  useEffect(() => {
    if (expandSig > 0) { setOpen(true); setFolderExpandSig((s) => s + 1); }
  }, [expandSig]);
  useEffect(() => {
    if (collapseSig > 0) { setOpen(false); setFolderCollapseSig((s) => s + 1); }
  }, [collapseSig]);

  async function saveCategoryName() {
    if (!draftName.trim() || draftName === category.name) { setEditingName(false); return; }
    setSaving(true);
    try   { await onRenameCategory(category.id, draftName.trim()); }
    catch { setDraftName(category.name); }
    finally { setSaving(false); setEditingName(false); }
  }

  async function handleAddFolder() {
    if (!newFolderName.trim()) return;
    setSaving(true);
    try { await onAddFolder(category.id, null, newFolderName.trim()); setNewFolderName(''); setAddFolderOpen(false); }
    finally { setSaving(false); }
  }

  const directLayers = category.layers || [];
  const folders      = category.folders || [];
  const totalLayers  = directLayers.length + folders.reduce((s, f) => s + countFolderLayers(f), 0);
  const hasChildren  = folders.length > 0 || directLayers.length > 0;

  function handleFoldersDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    const oldIdx = folders.findIndex((f) => f.id === active.id);
    const newIdx = folders.findIndex((f) => f.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorderFolders(folders, oldIdx, newIdx);
  }

  return (
    <div className="ca-tree-category">
      <div className="ca-tree-category-header">
        {dragHandle}
        <button className="ca-tree-chevron" onClick={() => setOpen((v) => !v)}
          aria-expanded={open} aria-label={open ? 'Recolher categoria' : 'Expandir categoria'}>
          {open ? '▾' : '▸'}
        </button>

        {editingName ? (
          <input className="ca-tree-edit-input" value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={saveCategoryName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveCategoryName();
              if (e.key === 'Escape') { setDraftName(category.name); setEditingName(false); }
            }}
            autoFocus disabled={saving} />
        ) : (
          <span className="ca-tree-category-name">{category.name}</span>
        )}

        <span className="ca-tree-count">{totalLayers}</span>
        <div className="ca-tree-actions">
          {hasChildren && <>
            <button className="ca-tree-expand-btn" title="Expandir tudo"
              onClick={() => { setOpen(true); setFolderExpandSig((s) => s + 1); }}>⊞</button>
            <button className="ca-tree-expand-btn" title="Recolher tudo"
              onClick={() => { setOpen(false); setFolderCollapseSig((s) => s + 1); }}>⊟</button>
          </>}
          <button className="ca-tree-btn ca-tree-btn--rename" title="Renomear categoria"
            onClick={() => setEditingName(true)}>✏️</button>
          <button className="ca-tree-btn" title="Adicionar pasta"
            onClick={() => setAddFolderOpen((v) => !v)}>+ Pasta</button>
          <button className="ca-tree-btn ca-tree-btn--danger" title="Remover categoria"
            onClick={() => onDeleteCategory(category.id)}>✕</button>
        </div>
      </div>

      {addFolderOpen && (
        <div className="ca-add-row">
          <input className="ca-input ca-input--inline" placeholder="Nome da pasta"
            value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddFolder(); }} />
          <button className="ca-confirm-btn ca-confirm-btn--sm"
            onClick={handleAddFolder} disabled={saving || !newFolderName.trim()}>Criar</button>
        </div>
      )}

      {open && (
        <>
          {directLayers.map((layer) => (
            <LayerRow key={layer.id} layer={layer} onDeleteAssignment={onDeleteAssignment} />
          ))}
          {folders.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFoldersDragEnd}>
              <SortableContext items={folders.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                {folders.map((folder) => (
                  <SortableTreeFolder key={folder.id} folder={folder} categoryId={category.id}
                    expandSig={folderExpandSig} collapseSig={folderCollapseSig}
                    onDeleteAssignment={onDeleteAssignment}
                    onDeleteFolder={onDeleteFolder}
                    onRenameFolder={onRenameFolder}
                    onAddFolder={onAddFolder}
                    onReorderFolders={onReorderFolders}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : null}
          {totalLayers === 0 && folders.length === 0 && (
            <p className="ca-tree-empty">Nenhuma camada atribuída.</p>
          )}
        </>
      )}
    </div>
  );
}

// ─── TreeGroup ────────────────────────────────────────────────────────────────

function TreeGroup({
  group, dragHandle,
  expandSig, collapseSig,
  onDeleteAssignment, onRenameGroup, onUpdateIcon, onRenameCategory,
  onDeleteGroup, onDeleteCategory, onAddCategory,
  onAddFolder, onDeleteFolder, onRenameFolder,
  onReorderCategories, onReorderFolders,
}) {
  const sensors = useSensors(useSensor(PointerSensor));
  const [open,          setOpen]          = useState(false);
  const [editingName,   setEditingName]   = useState(false);
  const [editingIcon,   setEditingIcon]   = useState(false);
  const [draftName,     setDraftName]     = useState(group.name);
  const [addCatOpen,    setAddCatOpen]    = useState(false);
  const [newCatName,    setNewCatName]    = useState('');
  const [saving,        setSaving]        = useState(false);
  const [catExpandSig,  setCatExpandSig]  = useState(0);
  const [catCollapseSig, setCatCollapseSig] = useState(0);

  /* Responde a sinais recursivos do pai (global) */
  useEffect(() => {
    if (expandSig > 0) { setOpen(true); setCatExpandSig((s) => s + 1); }
  }, [expandSig]);
  useEffect(() => {
    if (collapseSig > 0) { setOpen(false); setCatCollapseSig((s) => s + 1); }
  }, [collapseSig]);

  async function saveGroupName() {
    if (!draftName.trim() || draftName === group.name) { setEditingName(false); return; }
    setSaving(true);
    try   { await onRenameGroup(group.id, draftName.trim()); }
    catch { setDraftName(group.name); }
    finally { setSaving(false); setEditingName(false); }
  }

  async function handleIconChange(emoji) {
    setEditingIcon(false);
    if (emoji === group.icon) return;
    await onUpdateIcon(group.id, group.name, emoji);
  }

  async function handleAddCategory() {
    if (!newCatName.trim()) return;
    setSaving(true);
    try { await onAddCategory(group.id, newCatName.trim()); setNewCatName(''); setAddCatOpen(false); }
    finally { setSaving(false); }
  }

  function handleCatsDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    const oldIdx = group.categories.findIndex((c) => c.id === active.id);
    const newIdx = group.categories.findIndex((c) => c.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorderCategories(group.id, group.categories, oldIdx, newIdx);
  }

  const totalLayers = group.categories.reduce((s, c) => {
    return s + (c.layers || []).length + (c.folders || []).reduce((fs, f) => fs + countFolderLayers(f), 0);
  }, 0);

  const hasChildren = group.categories.length > 0;

  return (
    <div className="ca-tree-group">
      <div className="ca-tree-group-header">
        {dragHandle}
        <button className="ca-tree-chevron" onClick={() => setOpen((v) => !v)}
          aria-expanded={open} aria-label={open ? 'Recolher grupo' : 'Expandir grupo'}>
          {open ? '▾' : '▸'}
        </button>
        <button className="ca-tree-group-icon" onClick={() => setEditingIcon((v) => !v)}
          title="Clique para trocar o ícone" aria-label="Trocar ícone do grupo">
          <GroupIcon icon={group.icon} size={22} />
        </button>

        {editingName ? (
          <input className="ca-tree-edit-input" value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={saveGroupName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveGroupName();
              if (e.key === 'Escape') { setDraftName(group.name); setEditingName(false); }
            }}
            autoFocus disabled={saving} />
        ) : (
          <span className="ca-tree-group-name">{group.name}</span>
        )}

        <span className="ca-tree-count">{totalLayers}</span>
        <div className="ca-tree-actions">
          {hasChildren && <>
            <button className="ca-tree-expand-btn" title="Expandir tudo"
              onClick={() => { setOpen(true); setCatExpandSig((s) => s + 1); }}>⊞</button>
            <button className="ca-tree-expand-btn" title="Recolher tudo"
              onClick={() => { setOpen(false); setCatCollapseSig((s) => s + 1); }}>⊟</button>
          </>}
          <button className="ca-tree-btn ca-tree-btn--rename" title="Renomear grupo"
            onClick={() => setEditingName(true)}>✏️</button>
          <button className="ca-tree-btn" title="Adicionar categoria"
            onClick={() => setAddCatOpen((v) => !v)}>+Cat</button>
          <button className="ca-tree-btn ca-tree-btn--danger" title="Remover grupo"
            onClick={() => onDeleteGroup(group.id)}>✕</button>
        </div>
      </div>

      {editingIcon && (
        <div className="ca-icon-picker-wrap">
          <p className="ca-label" style={{ margin: '0 0 4px' }}>Escolha o ícone:</p>
          <IconPicker value={group.icon || DEFAULT_ICON} onChange={handleIconChange} />
        </div>
      )}

      {addCatOpen && (
        <div className="ca-add-row">
          <input className="ca-input ca-input--inline" placeholder="Nome da categoria"
            value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); }} />
          <button className="ca-confirm-btn ca-confirm-btn--sm"
            onClick={handleAddCategory} disabled={saving || !newCatName.trim()}>Criar</button>
        </div>
      )}

      {open && group.categories.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCatsDragEnd}>
          <SortableContext items={group.categories.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {group.categories.map((cat) => (
              <SortableTreeCategory key={cat.id} category={cat}
                expandSig={catExpandSig} collapseSig={catCollapseSig}
                onDeleteAssignment={onDeleteAssignment}
                onRenameCategory={onRenameCategory}
                onDeleteCategory={onDeleteCategory}
                onAddFolder={onAddFolder}
                onDeleteFolder={onDeleteFolder}
                onRenameFolder={onRenameFolder}
                onReorderFolders={onReorderFolders}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// ─── Sortable wrappers ────────────────────────────────────────────────────────

function SortableTreeFolder({ folder, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: folder.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const dragHandle = (
    <button className="ca-drag-handle" {...attributes} {...listeners}
      title="Arrastar para reordenar" aria-label="Arrastar pasta">⠿</button>
  );
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'ca-sortable--dragging' : ''}>
      <TreeFolder folder={folder} dragHandle={dragHandle} {...props} />
    </div>
  );
}

function SortableTreeCategory({ category, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: category.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const dragHandle = (
    <button className="ca-drag-handle" {...attributes} {...listeners}
      title="Arrastar para reordenar" aria-label="Arrastar categoria">⠿</button>
  );
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'ca-sortable--dragging' : ''}>
      <TreeCategory category={category} dragHandle={dragHandle} {...props} />
    </div>
  );
}

function SortableTreeGroup({ group, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: group.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const dragHandle = (
    <button className="ca-drag-handle" {...attributes} {...listeners}
      title="Arrastar para reordenar" aria-label="Arrastar grupo">⠿</button>
  );
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'ca-sortable--dragging' : ''}>
      <TreeGroup group={group} dragHandle={dragHandle} {...props} />
    </div>
  );
}

// ─── CatalogAdmin principal ───────────────────────────────────────────────────

function CatalogAdmin() {
  const sensors = useSensors(useSensor(PointerSensor));
  const [tree,             setTree]             = useState([]);
  const [pending,          setPending]          = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [newGroupName,     setNewGroupName]     = useState('');
  const [newGroupIcon,     setNewGroupIcon]     = useState(DEFAULT_ICON);
  const [addingGroup,      setAddingGroup]      = useState(false);
  const [showIconPicker,   setShowIconPicker]   = useState(false);
  const [globalExpandSig,  setGlobalExpandSig]  = useState(0);
  const [globalCollapseSig, setGlobalCollapseSig] = useState(0);

  const reloadTree = useCallback(async () => {
    const res = await catalogAPI.getTree();
    setTree(res.data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [treeRes, pendingRes] = await Promise.all([catalogAPI.getTree(), catalogAPI.getPending()]);
      setTree(treeRes.data);
      setPending(pendingRes.data);
    } catch (e) {
      console.error('CatalogAdmin: load error', e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleGroupCreated(group) {
    setTree((prev) => [...prev, { ...group, categories: [] }]);
  }

  function handleCategoryCreated(groupId, category) {
    setTree((prev) => prev.map((g) => g.id === groupId
      ? { ...g, categories: [...g.categories, { ...category, layers: [], folders: [] }] }
      : g));
  }

  function handleAssigned(layerId) {
    setPending((prev) => prev.filter((l) => l.id !== layerId));
    reloadTree().catch(() => {});
  }

  async function handleDeleteAssignment(assignmentId, layerName) {
    if (!window.confirm(`Remover "${layerName}" do catálogo?`)) return;
    try {
      await catalogAPI.unassign(assignmentId);
      await reloadTree();
      catalogAPI.getPending().then((res) => setPending(res.data)).catch(() => {});
    } catch (e) { alert(e.response?.data?.error || 'Erro ao remover atribuição.'); }
  }

  async function handleRenameGroup(groupId, name) {
    await catalogAPI.updateGroup(groupId, { name });
    setTree((prev) => prev.map((g) => g.id === groupId ? { ...g, name } : g));
  }

  async function handleUpdateIcon(groupId, name, icon) {
    await catalogAPI.updateGroup(groupId, { name, icon });
    setTree((prev) => prev.map((g) => g.id === groupId ? { ...g, icon } : g));
  }

  async function handleRenameCategory(categoryId, name) {
    await catalogAPI.updateCategory(categoryId, { name });
    setTree((prev) => prev.map((g) => ({
      ...g,
      categories: g.categories.map((c) => c.id === categoryId ? { ...c, name } : c),
    })));
  }

  async function handleDeleteGroup(groupId) {
    const group = tree.find((g) => g.id === groupId);
    if (!window.confirm(`Remover grupo "${group?.name}" e todas as suas categorias?`)) return;
    try {
      await catalogAPI.deleteGroup(groupId);
      setTree((prev) => prev.filter((g) => g.id !== groupId));
      catalogAPI.getPending().then((res) => setPending(res.data)).catch(() => {});
    } catch (e) { alert(e.response?.data?.error || 'Erro ao remover grupo.'); }
  }

  async function handleDeleteCategory(categoryId) {
    if (!window.confirm('Remover esta categoria e todas as pastas e camadas atribuídas?')) return;
    try {
      await catalogAPI.deleteCategory(categoryId);
      setTree((prev) => prev.map((g) => ({ ...g, categories: g.categories.filter((c) => c.id !== categoryId) })));
      catalogAPI.getPending().then((res) => setPending(res.data)).catch(() => {});
    } catch (e) { alert(e.response?.data?.error || 'Erro ao remover categoria.'); }
  }

  async function handleAddCategory(groupId, name) {
    const res = await catalogAPI.createCategory({ group_id: groupId, name });
    handleCategoryCreated(groupId, res.data);
  }

  async function handleAddFolder(categoryId, parentFolderId, name) {
    await catalogAPI.createFolder({ category_id: categoryId, parent_id: parentFolderId, name });
    await reloadTree();
  }

  async function handleDeleteFolder(folderId) {
    if (!window.confirm('Remover esta pasta e sub-pastas? As camadas voltarão para a categoria.')) return;
    try {
      await catalogAPI.deleteFolder(folderId);
      await reloadTree();
      catalogAPI.getPending().then((res) => setPending(res.data)).catch(() => {});
    } catch (e) { alert(e.response?.data?.error || 'Erro ao remover pasta.'); }
  }

  async function handleRenameFolder(folderId, name) {
    await catalogAPI.updateFolder(folderId, { name });
    await reloadTree();
  }

  /* ── Reordenação via drag-and-drop ── */

  function handleGroupsDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    const oldIdx = tree.findIndex((g) => g.id === active.id);
    const newIdx = tree.findIndex((g) => g.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(tree, oldIdx, newIdx);
    setTree(reordered);
    Promise.all(
      reordered.map((g, i) => catalogAPI.updateGroup(g.id, { name: g.name, icon: g.icon || null, sort_order: i }))
    ).catch(() => reloadTree());
  }

  async function handleReorderCategories(groupId, categoriesArray, oldIdx, newIdx) {
    const reordered = arrayMove(categoriesArray, oldIdx, newIdx);
    setTree((prev) => prev.map((g) => g.id === groupId ? { ...g, categories: reordered } : g));
    try {
      await Promise.all(reordered.map((c, i) => catalogAPI.updateCategory(c.id, { name: c.name, sort_order: i })));
    } catch (e) { console.error('Erro ao reordenar categorias', e); reloadTree(); }
  }

  async function handleReorderFolders(foldersArray, oldIdx, newIdx) {
    const reordered = arrayMove(foldersArray, oldIdx, newIdx);
    try {
      await Promise.all(reordered.map((f, i) => catalogAPI.updateFolder(f.id, { name: f.name, sort_order: i })));
      await reloadTree();
    } catch (e) { console.error('Erro ao reordenar pastas', e); }
  }

  async function handleCreateGroup() {
    if (!newGroupName.trim()) return;
    try {
      setAddingGroup(true);
      const res = await catalogAPI.createGroup({ name: newGroupName.trim(), icon: newGroupIcon });
      handleGroupCreated(res.data);
      setNewGroupName('');
      setNewGroupIcon(DEFAULT_ICON);
      setShowIconPicker(false);
    } catch (e) { alert(e.response?.data?.error || 'Erro ao criar grupo.'); }
    finally { setAddingGroup(false); }
  }

  if (loading) return <p className="ca-loading">Carregando…</p>;

  return (
    <div className="catalog-admin">
      {/* Left: camadas pendentes */}
      <div className="ca-panel ca-panel--pending">
        <div className="ca-panel-header">
          <h3 className="ca-panel-title">Pendentes</h3>
          <span className="ca-panel-count">{pending.length}</span>
        </div>
        {pending.length === 0 ? (
          <p className="ca-empty">Todas as camadas estão organizadas.</p>
        ) : (
          <div className="ca-pending-list">
            {pending.map((layer) => (
              <PendingRow key={layer.id} layer={layer} groups={tree}
                onAssigned={handleAssigned}
                onGroupCreated={handleGroupCreated}
                onCategoryCreated={handleCategoryCreated}
              />
            ))}
          </div>
        )}
      </div>

      {/* Right: árvore organizada */}
      <div className="ca-panel ca-panel--tree">
        <div className="ca-panel-header">
          <h3 className="ca-panel-title">Catálogo organizado</h3>
          <span className="ca-panel-count">{tree.length} grupos</span>
          {tree.length > 0 && (
            <div className="ca-tree-global-actions">
              <button className="ca-tree-expand-btn" title="Expandir tudo"
                onClick={() => setGlobalExpandSig((s) => s + 1)}>⊞ Expandir tudo</button>
              <button className="ca-tree-expand-btn" title="Recolher tudo"
                onClick={() => setGlobalCollapseSig((s) => s + 1)}>⊟ Recolher tudo</button>
            </div>
          )}
        </div>

        <div className="ca-add-group-form">
          <div className="ca-add-group-row">
            <button type="button" className="ca-new-group-icon-btn"
              onClick={() => setShowIconPicker((v) => !v)}
              title="Escolher ícone" aria-label="Escolher ícone do grupo">
              <GroupIcon icon={newGroupIcon} size={22} />
            </button>
            <input className="ca-input" placeholder="Nome do novo grupo…"
              value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateGroup(); }} />
            <button className="ca-confirm-btn" onClick={handleCreateGroup}
              disabled={addingGroup || !newGroupName.trim()}>+ Grupo</button>
          </div>
          {showIconPicker && (
            <div className="ca-icon-picker-wrap">
              <IconPicker value={newGroupIcon}
                onChange={(e) => { setNewGroupIcon(e); setShowIconPicker(false); }} />
            </div>
          )}
        </div>

        {tree.length === 0 ? (
          <p className="ca-empty">Nenhum grupo criado. Crie um grupo acima e atribua camadas.</p>
        ) : (
          <div className="ca-tree-list">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGroupsDragEnd}>
              <SortableContext items={tree.map((g) => g.id)} strategy={verticalListSortingStrategy}>
                {tree.map((group) => (
                  <SortableTreeGroup key={group.id} group={group}
                    expandSig={globalExpandSig} collapseSig={globalCollapseSig}
                    onDeleteAssignment={handleDeleteAssignment}
                    onRenameGroup={handleRenameGroup}
                    onUpdateIcon={handleUpdateIcon}
                    onRenameCategory={handleRenameCategory}
                    onDeleteGroup={handleDeleteGroup}
                    onDeleteCategory={handleDeleteCategory}
                    onAddCategory={handleAddCategory}
                    onAddFolder={handleAddFolder}
                    onDeleteFolder={handleDeleteFolder}
                    onRenameFolder={handleRenameFolder}
                    onReorderCategories={handleReorderCategories}
                    onReorderFolders={handleReorderFolders}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    </div>
  );
}

export default CatalogAdmin;
